import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, CreditCard, Package, ShoppingBag } from 'lucide-react';
import { ShopDemo } from '../demos/ShopDemo';
import { CtaLink, Headline, Kicker, Reveal, Section, Spec } from '../ui';
import { PAYMENT_META, PAYMENT_METHODS } from '@/lib/payments';
import { PLANS } from '@/lib/plans';
import { cn } from '@/lib/utils';
import { JourneyStory } from '../ScrollStory';
import { ProductPreview } from '../demos/BrandPreviews';

const STEPS = [
  { k: 'Browse', d: 'Product grid on the card. Tap for the detail sheet: photo, badges, bullets, options.' },
  { k: 'Order', d: 'Name, email, note. Pick a way to pay. The order is saved with a number.' },
  { k: 'Pay', d: 'Pre-filled Venmo / Cash App / PayPal link, Zelle instructions, or Stripe Checkout.' },
  { k: 'Confirm', d: 'Card orders confirm themselves. P2P: the customer taps "I sent it", you confirm.' },
  { k: 'Fulfil', d: 'Mark fulfilled in Orders. They get an email and a public tracking page.' },
];

export function ShopPage() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s + 1) % STEPS.length), 2600);
    return () => clearInterval(t);
  }, []);

  return (
    <>
      <Section className="play-subhero play-subhero--shop pb-20 pt-16">
        <div className="grid gap-12 lg:grid-cols-[1fr_1fr] lg:items-start">
          <Reveal className="lg:sticky lg:top-28">
            <Kicker>Shop · zero-percent selling</Kicker>
            <h1 className="play-subhero__title mt-4">
              Sell it right there, in the <em>introduction.</em>
            </h1>
            <p className="play-subhero__body mt-6 max-w-xl">
              Prints, mugs, sessions, salsa, whatever you make. Add products to your card, switch
              the shop on, and the people you meet can order before they've put their phone away.
              You get paid directly. Haphe takes nothing.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaLink to="/pricing?plan=shop" className="play-primary-cta">
                Shop plan · ${PLANS.shop.monthlyCents / 100}/mo
              </CtaLink>
              <CtaLink to="/signup" variant="ghost">
                Start free
              </CtaLink>
            </div>
            <p className="mt-6 text-sm text-ink-muted">Try it on the right. Real component, nothing saved.</p>
          </Reveal>
          <Reveal delay={0.1} className="play-product-stage play-product-stage--blue">
            <ShopDemo className="mx-auto max-w-md" />
            <div id="try-shop" />
          </Reveal>
        </div>
      </Section>

      {/* Products */}
      <JourneyStory kind="shop" />
      <Section className="play-page-section py-24"><div className="brand-preview-layout"><div>
        <Reveal className="max-w-2xl">
          <Kicker>Products</Kicker>
          <Headline className="mt-4">Every product, exactly as you'd describe it.</Headline>
        </Reveal>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {[
            ['Photo, price, description', 'A square photo, a price in dollars, and a description that shows in full on the detail sheet.'],
            ['Badges', 'Up to two short labels on the photo. HOT. MILD. Large. Limited. New. Whatever the thing needs.'],
            ['Bullet points', 'Dimensions, materials, lead time, what is included. One per line, shown as a checklist.'],
            ['Options', 'Size, color, heat, framing. Each option is a button group or a dropdown. Customers must choose before they add.'],
            ['In stock / sold out', 'Sold-out products stay visible, greyed, so people know it exists and can ask.'],
            ['Hidden until ready', 'New products start hidden. Build the listing in private, flip Visible when it is done.'],
          ].map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.04}>
              <div className="play-block-row h-full rounded-2xl border border-bone-deep bg-surface p-5">
                <p className="text-sm font-medium tracking-tight">{t}</p>
                <p className="mt-2 text-sm leading-relaxed text-ink-muted">{d}</p>
              </div>
            </Reveal>
          ))}
        </div></div><ProductPreview /></div>
      </Section>

      {/* Payments */}
      <Section dark className="play-page-dark py-24">
        <Reveal className="max-w-2xl">
          <Kicker gold>Getting paid</Kicker>
          <Headline className="mt-4 text-bone">Your money, your accounts, no middleman.</Headline>
          <p className="mt-5 text-base leading-relaxed text-bone/65">
            Switch on any of these. Peer-to-peer rails pay you directly with the order number in the
            memo; you confirm from the inbox. Card payments run through your own Stripe account and
            confirm themselves.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {PAYMENT_METHODS.map((m, i) => {
            const meta = PAYMENT_META[m];
            return (
              <Reveal key={m} delay={i * 0.04}>
                <div className="flex h-full items-start gap-3 rounded-2xl border border-bone/10 bg-white/[0.03] p-5">
                  <span className="brand-payment-logo"><img src={`/marketing/payments/${m}.svg`} alt={m === 'stripe' ? 'Stripe' : meta.name} width={32} height={32} loading="lazy"/></span>
                  <div>
                    <p className="text-sm font-medium tracking-tight text-bone">
                      {m === 'stripe' ? 'Card · Apple Pay · Google Pay' : meta.name}
                      {meta.automatic && <span className="ml-2 rounded-full bg-[oklch(0.8_0.13_78_/_0.2)] px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest mk-gold">auto-confirms</span>}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-bone/60">{meta.hint}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
        <div className="brand-wallet-logos"><img src="/marketing/payments/applepay.svg" alt="Apple Pay" width={62} height={32}/><img src="/marketing/payments/googlepay.svg" alt="Google Pay" width={62} height={32}/><span>Available through Stripe</span></div>
        <Reveal className="mt-10 grid gap-4 md:grid-cols-2">
          <Spec dark title="Stripe, done properly" rows={[['account', 'yours (Stripe Connect Express)'], ['setup', 'about five minutes'], ['fees', "Stripe's, paid by you"], ['Haphe cut', '0%'], ['disputes', 'handled in your Stripe']]} />
          <Spec dark title="Peer-to-peer rails" rows={[['Venmo', 'pre-filled amount + memo'], ['Cash App', 'cash.app/$you/amount'], ['PayPal', 'paypal.me/you/amount'], ['Zelle', 'instructions + memo'], ['Square', 'your payment link'], ['Cash', 'pay on delivery or at pickup']]} />
        </Reveal>
      </Section>

      {/* Lifecycle */}
      <Section className="play-page-section play-page-section--warm py-24">
        <Reveal className="max-w-2xl">
          <Kicker>The order, start to finish</Kicker>
          <Headline className="mt-4">Five steps. You touch two.</Headline>
        </Reveal>
        <div className="mt-12 grid gap-6 lg:grid-cols-[1fr_1.2fr] lg:items-center">
          <ol className="space-y-2">
            {STEPS.map((s, i) => (
              <li key={s.k}>
                <button
                  onClick={() => setStep(i)}
                  className={cn(
                    'play-step flex w-full items-start gap-4 rounded-2xl border p-4 text-left transition-colors',
                    step === i ? 'play-step--active border-ink bg-surface' : 'border-bone-deep bg-transparent hover:bg-surface/60',
                  )}
                >
                  <span className={cn('mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-mono text-[10px]', step === i ? 'bg-ink text-bone' : 'bg-bone-warm text-ink-muted')}>
                    {i + 1}
                  </span>
                  <span>
                    <span className="block text-sm font-medium tracking-tight">{s.k}</span>
                    <span className="block text-sm leading-relaxed text-ink-muted">{s.d}</span>
                  </span>
                </button>
              </li>
            ))}
          </ol>
          <Reveal>
            <motion.div key={step} initial={{ opacity: 0, y: 8, rotateX: -4 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} className="play-lifecycle-stage rounded-3xl border border-bone-deep bg-surface p-6">
              {step === 0 && <MockGrid />}
              {step === 1 && <MockOrder />}
              {step === 2 && <MockPay />}
              {step === 3 && <MockConfirm />}
              {step === 4 && <MockFulfil />}
            </motion.div>
          </Reveal>
        </div>
      </Section>

      <Section dark className="play-page-dark play-page-dark--orange py-24 text-center">
        <Reveal>
          <Headline size="xl" className="mx-auto max-w-3xl text-bone">
            ${PLANS.shop.monthlyCents / 100} a month. Zero percent of your sales.
          </Headline>
          <p className="mx-auto mt-6 max-w-md text-base text-bone/65">
            The Shop plan includes everything in Pro. Most marketplaces take 5 to 15 percent; a
            flat month is cheaper after your first few orders.
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <CtaLink to="/pricing?plan=shop" variant="gold">
              See the Shop plan
            </CtaLink>
          </div>
        </Reveal>
      </Section>
    </>
  );
}

// ─── Lifecycle mocks ──────────────────────────────────────────────────────────
function Row({ l, r }: { l: string; r: string }) {
  return (
    <div className="flex items-center justify-between border-b border-bone-deep py-2 text-sm last:border-b-0">
      <span className="text-ink-muted">{l}</span>
      <span className="font-medium tabular-nums">{r}</span>
    </div>
  );
}
function MockGrid() {
  return (
    <div>
      <p className="mk-kicker text-ink-muted">Card · Shop section</p>
      <div className="mt-3 grid grid-cols-2 gap-3">
        {[['Salsa · Hot', '$9', 'HOT'], ['Salsa · Mild', '$9', 'MILD']].map(([n, p, b]) => (
          <div key={n} className="overflow-hidden rounded-xl border border-bone-deep">
            <div className="relative aspect-square bg-gradient-to-br from-[#e07628]/40 to-[#3a4a3f]/40">
              <span className="absolute left-2 top-2 rounded-full bg-accent px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-bone">{b}</span>
            </div>
            <div className="flex items-center justify-between p-3 text-sm">
              <span>{n}</span>
              <span className="font-medium">{p}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
function MockOrder() {
  return (
    <div>
      <p className="mk-kicker text-ink-muted">Checkout sheet</p>
      <div className="mt-3">
        <Row l="Salsa · Hot × 2 (Size: 16 oz)" r="$18.00" />
        <Row l="Salsa · Mild × 1" r="$9.00" />
        <Row l="Total" r="$27.00" />
      </div>
      <div className="mt-3 grid grid-cols-3 gap-1.5">
        {['Card', 'Venmo', 'Zelle'].map((m, i) => (
          <span key={m} className={cn('rounded-md border px-2 py-1.5 text-center text-xs', i === 1 ? 'border-ink bg-ink text-bone' : 'border-bone-deep')}>{m}</span>
        ))}
      </div>
    </div>
  );
}
function MockPay() {
  return (
    <div>
      <p className="mk-kicker text-ink-muted">Pay · Venmo</p>
      <p className="mt-2 text-3xl font-medium tracking-tightest">$27.00</p>
      <div className="mt-3 flex h-11 items-center justify-center rounded-full bg-[#008CFF] text-sm font-medium text-white">Pay with Venmo</div>
      <div className="mt-2 rounded-lg border border-bone-deep bg-bone px-3 py-2 font-mono text-xs">Order #1042 — Alex Carter</div>
    </div>
  );
}
function MockConfirm() {
  return (
    <div>
      <p className="mk-kicker text-ink-muted">Orders inbox</p>
      <div className="mt-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3">
        <p className="text-sm font-medium">#1042 · Jordan Lee · $27.00</p>
        <p className="text-xs text-ink-muted">Venmo · says they paid 2m ago</p>
        <div className="mt-2 flex h-9 items-center justify-center gap-1.5 rounded-md bg-action text-xs font-medium text-bone"><Check className="h-3.5 w-3.5" /> Confirm payment received</div>
      </div>
      <p className="mt-3 flex items-center gap-2 text-xs text-ink-muted"><CreditCard className="h-3.5 w-3.5" /> Card orders skip this step entirely.</p>
    </div>
  );
}
function MockFulfil() {
  return (
    <div>
      <p className="mk-kicker text-ink-muted">Public order page</p>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {['Placed', 'Paid', 'Fulfilled'].map((s) => (
          <div key={s} className="flex flex-col items-center gap-1 rounded-xl border border-accent/40 bg-accent/10 py-3 text-accent">
            {s === 'Fulfilled' ? <Package className="h-4 w-4" /> : <Check className="h-4 w-4" />}
            <span className="font-mono text-[9px] uppercase tracking-widest">{s}</span>
          </div>
        ))}
      </div>
      <p className="mt-3 flex items-center gap-2 text-xs text-ink-muted"><ShoppingBag className="h-3.5 w-3.5" /> Linked from every email. Pay-again if they left early.</p>
    </div>
  );
}
